
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Package, User, Phone, MapPin, Clock, Printer, Edit, Trash2 } from 'lucide-react';
import { Order } from '@/types';

interface OrderDetailsProps {
  order: Order;
}

const OrderDetails: React.FC<OrderDetailsProps> = ({ order }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Đã giao': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Đang giao': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Chờ xử lý': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'Bị hủy': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold">{order.id}</h3>
          <p className="text-sm text-muted-foreground">
            {new Date(order.timestamp).toLocaleString('vi-VN')}
          </p>
        </div>
        <Badge className={getStatusColor(order.status)}>
          {order.status}
        </Badge>
      </div>

      {/* Sender Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <User className="h-4 w-4" />
            Người gửi
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm bg-blue-50 dark:bg-blue-950/20">
          <div className="flex items-center gap-2">
            <span className="font-medium">{order.sender}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Phone className="h-3 w-3" />
            <a 
              href={`tel:${order.senderPhone}`}
              className="text-blue-600 hover:underline cursor-pointer"
            >
              {order.senderPhone}
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Recipient Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Người nhận
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm bg-green-50 dark:bg-green-950/20">
          <div className="flex items-center gap-2">
            <span className="font-medium">{order.recipient}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Phone className="h-3 w-3" />
            <a 
              href={`tel:${order.recipientPhone}`}
              className="text-blue-600 hover:underline cursor-pointer"
            >
              {order.recipientPhone}
            </a>
          </div>
          <div className="text-muted-foreground">
            <p>{order.address}</p>
          </div>
        </CardContent>
      </Card>

      {/* Package Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-4 w-4" />
            Thông tin gói hàng
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm bg-orange-50 dark:bg-orange-950/20">
          {order.customOrderId && (
            <div>
              <span className="text-muted-foreground">Mã đơn tùy chọn:</span>
              <p className="font-medium">{order.customOrderId}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-muted-foreground">Cân nặng:</span>
              <p className="font-medium">{order.weight} g</p>
            </div>
            <div>
              <span className="text-muted-foreground">Kích thước:</span>
              <p className="font-medium">{order.dimensions} cm</p>
            </div>
          </div>
          <div>
            <span className="text-muted-foreground">Dịch vụ:</span>
            <p className="font-medium">{order.service}</p>
          </div>
          {order.declaredValue && (
            <div>
              <span className="text-muted-foreground">Giá trị khai báo:</span>
              <p className="font-medium">{formatCurrency(order.declaredValue)}</p>
            </div>
          )}
          <div>
            <span className="text-muted-foreground">Sản phẩm:</span>
            <div className="space-y-1 mt-1">
              {order.products.map((product) => (
                <div key={product.id} className="flex justify-between items-center p-2 bg-background rounded border">
                  <span className="text-sm">{product.name} x{product.quantity}</span>
                  <span className="text-sm font-medium">{formatCurrency(product.value * product.quantity)}</span>
                </div>
              ))}
            </div>
          </div>
          {order.driverNotes && (
            <div>
              <span className="text-muted-foreground">Ghi chú cho tài xế:</span>
              <p className="font-medium text-yellow-700 dark:text-yellow-300">{order.driverNotes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* COD Info */}
      {order.codAmount > 0 && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Tiền thu hộ:</span>
              <span className="font-bold text-primary">
                {formatCurrency(order.codAmount)}
              </span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm text-muted-foreground">Người trả phí:</span>
              <span className="text-sm font-medium">
                {order.paymentBy === 'sender' ? 'Người gửi' : 'Người nhận'}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tracking History */}
      {order.trackingHistory && order.trackingHistory.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Lịch sử vận chuyển
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {order.trackingHistory.map((event, index) => (
                <div key={index} className="flex gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <div className="flex-1 text-sm">
                    <p className="font-medium">{event.status}</p>
                    <p className="text-muted-foreground">
                      {new Date(event.time).toLocaleString('vi-VN')} • {event.location}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Separator />

      {/* Actions */}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="flex-1">
          <Edit className="h-4 w-4 mr-2" />
          Chỉnh sửa
        </Button>
        <Button size="sm" variant="outline" className="flex-1">
          <Printer className="h-4 w-4 mr-2" />
          In tem
        </Button>
        <Button size="sm" variant="destructive" className="flex-1">
          <Trash2 className="h-4 w-4 mr-2" />
          Xóa
        </Button>
      </div>
    </div>
  );
};

export default OrderDetails;
